/**
 * Suppresses known-harmless global errors so the dev overlay does not steal focus.
 * Installed from main.tsx before React / Monaco load (capture phase beats bubbling overlay).
 *
 * Covers: ResizeObserver noise, Monaco dispose/cancel, extension fetch failures,
 * axios-style rejections already handled at call sites.
 */
const FLAG = "__ncGlobalErrorHandlers";

function shouldSuppressMessage(msg: unknown): boolean {
  return (
    typeof msg === "string" &&
    (msg.includes("ResizeObserver") || msg.includes("TextModel got disposed"))
  );
}

function noisyFetchLike(msg: string): boolean {
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError when attempting to fetch") ||
    msg.includes("Load failed")
  );
}

/** Monaco often rejects with "Canceled" during model/editor teardown. */
function monacoCanceledUnhandled(msg: string, stack: string): boolean {
  if (!msg || (msg.trim() !== "Canceled" && !msg.startsWith("Canceled:"))) return false;
  return stack.includes("editor.api") || stack.includes("/vs/");
}

/** Injected/eval'd scripts (extensions, userscripts, dev tools) appear as
 *  `<anonymous>` frames at the top of the stack — they have no source URL,
 *  so they cannot be our code. Suppress fetch failures originating there. */
function isInjectedScript(stack: string): boolean {
  const topFrame = stack.split("\n")[1] ?? "";
  return topFrame.includes("<anonymous>:");
}

function monacoCanceledErrorEvent(msg: string, filename: string | undefined): boolean {
  if (!msg || (msg.trim() !== "Canceled" && !msg.startsWith("Canceled:"))) return false;
  return !!filename && (filename.includes("editor.api") || filename.includes("/vs/"));
}

function isAxiosLike(r: unknown): boolean {
  return (
    !!r &&
    typeof r === "object" &&
    r !== null &&
    ("isAxiosError" in r || "response" in r || "config" in r || "request" in r)
  );
}

function rejectionMessageAndStack(reason: unknown): { msg: string; stack: string } {
  if (reason && typeof reason === "object") {
    const o = reason as Record<string, unknown>;
    const msg =
      typeof o.message === "string" ? o.message : String(reason ?? "");
    const stack = typeof o.stack === "string" ? o.stack : "";
    return { msg, stack };
  }
  return { msg: String(reason ?? ""), stack: "" };
}

function suppressEvent(e: Event): void {
  e.stopImmediatePropagation();
  e.preventDefault();
}

export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & Record<string, boolean>;
  if (w[FLAG]) return;
  w[FLAG] = true;

  window.addEventListener(
    "error",
    (e: ErrorEvent) => {
      const msg = e.message || "";
      if (shouldSuppressMessage(msg)) {
        suppressEvent(e);
        return;
      }
      if (noisyFetchLike(msg)) {
        const stack = typeof (e.error as { stack?: unknown } | null)?.stack === "string"
          ? (e.error as { stack: string }).stack
          : "";
        if (isInjectedScript(stack)) {
          suppressEvent(e);
          return;
        }
        if (e.filename && !e.filename.startsWith(location.origin)) {
          e.preventDefault();
          return;
        }
      }
      if (monacoCanceledErrorEvent(msg, e.filename)) {
        e.preventDefault();
        return;
      }
      if (msg === "[object Object]" || (!msg && isAxiosLike(e.error))) {
        suppressEvent(e);
      }
    },
    true,
  );

  window.addEventListener(
    "unhandledrejection",
    (e: PromiseRejectionEvent) => {
      const { msg, stack } = rejectionMessageAndStack(e.reason);
      if (monacoCanceledUnhandled(msg, stack)) {
        e.preventDefault();
        return;
      }
      if (shouldSuppressMessage(msg)) {
        suppressEvent(e);
        return;
      }
      if (isAxiosLike(e.reason)) {
        suppressEvent(e);
        return;
      }
      if (!noisyFetchLike(msg)) return;
      if (isInjectedScript(stack)) {
        suppressEvent(e);
        return;
      }
      if (!stack.includes(location.origin) || stack.includes("HTMLImageElement")) {
        e.preventDefault();
      }
    },
    true,
  );

  const prev = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (shouldSuppressMessage(message)) return true;
    if (message === "[object Object]") return true;
    return prev
      ? prev(message, source, lineno, colno, error)
      : false;
  };
}

installGlobalErrorHandlers();
